import type { Witness } from '../store/booksStore'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'
import { getLedgerSafeCellState } from './ledgerSafeRowEditing'

export const sortLedgerSafeSuggestionRows = (rows: AnchoredRow[]): AnchoredRow[] => [...rows].sort((left, right) => {
  if (left.position !== right.position) {
    return left.position - right.position
  }
  if (left.labelNumber !== right.labelNumber) {
    return left.labelNumber - right.labelNumber
  }
  return left.id.localeCompare(right.id)
})

export const asLedgerSafeSuggestionString = (value: unknown): string => String(value || '').trim()

export const asLedgerSafeSuggestionStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(item => asLedgerSafeSuggestionString(item)).filter(Boolean)
    : []

export const getLedgerSafeSuggestionWitnessLabel = (witness: Witness | undefined, fallback: string): string =>
  witness?.sigla || witness?.description || fallback

export const getLedgerSafeActiveMappingText = (row: AnchoredRow | undefined, witnessId: string): string =>
  row?.unitMappings.find(mapping => mapping.witnessId === witnessId && !mapping.excluded)?.textSnapshot || ''

export const getLedgerSafeWitnessRowText = (rows: AnchoredRow[], rowId: string, witnessId: string): string =>
  getLedgerSafeActiveMappingText(rows.find(row => row.id === rowId), witnessId)

export const getLedgerSafeWitnessWindowText = (rows: AnchoredRow[], rowIds: string[], witnessId: string): string =>
  rowIds.map(rowId => getLedgerSafeWitnessRowText(rows, rowId, witnessId)).filter(Boolean).join(' ')

export const getLedgerSafeEventRowText = (
  event: LedgerSafeEditorialEvent,
  snapshotKey: 'beforeRows' | 'afterRows',
  witnessId: string,
  rowId: string
): string =>
  event[snapshotKey]
    ?.find(row => row.id === rowId)
    ?.unitMappings
    ?.find(mapping => mapping.witnessId === witnessId && !mapping.excluded)
    ?.textSnapshot || ''

export const findLedgerSafeNextMappedRowId = (rows: AnchoredRow[], leftRowId: string, witnessId: string): string | null => {
  const leftIndex = rows.findIndex(row => row.id === leftRowId)
  if (leftIndex < 0) {
    return null
  }
  for (let index = leftIndex + 1; index < rows.length; index += 1) {
    if (getLedgerSafeCellState(rows[index], witnessId) === 'mapped') {
      return rows[index].id
    }
  }
  return null
}

export const ledgerSafeSuggestionRowHasActiveMapping = (row: AnchoredRow | undefined): boolean =>
  Boolean(row?.unitMappings.some(mapping => !mapping.excluded))

export const findLedgerSafeAdjacentRowForPlacement = (
  rows: AnchoredRow[],
  rowId: string,
  placement: 'above' | 'below'
): AnchoredRow | null => {
  const anchorIndex = rows.findIndex(row => row.id === rowId)
  if (anchorIndex < 0) {
    return null
  }
  const targetIndex = placement === 'above' ? anchorIndex - 1 : anchorIndex + 1
  return rows[targetIndex] || null
}
